import { RootState } from "@store/index";
import { createSelector } from "reselect";
import { TestSuiteState, TestSuiteItem, initialTestSuiteState } from "./testSuiteSlice";
import { isOverviewEnabled } from "@utils/OverviewUtils";

const defaultTestSuiteState: TestSuiteState = {
  ...initialTestSuiteState,
};

const testSuiteState = (state: RootState): TestSuiteState =>
  state.testSuite ?? defaultTestSuiteState;

export const selectTestSuiteTests = createSelector([testSuiteState], (state) => state.tests);

export const selectActiveTestIndex = createSelector(
  [testSuiteState],
  (state) => state.activeTestIndex,
);

export const selectSelectedTestIndex = createSelector(
  [testSuiteState],
  (state) => state.selectedTestIndex,
);

export const selectActiveTest = createSelector(
  [selectTestSuiteTests, selectActiveTestIndex],
  (tests, activeIndex) => (activeIndex !== null ? tests[activeIndex] || null : null),
);

export const selectSelectedTest = createSelector(
  [selectTestSuiteTests, selectSelectedTestIndex],
  (tests, selectedIndex) => (selectedIndex !== null ? tests[selectedIndex] || null : null),
);

export const selectTestById = (id: string) =>
  createSelector([selectTestSuiteTests], (tests) => tests.find((test) => test.id === id) || null);

export const selectTestSuiteLength = createSelector(
  [selectTestSuiteTests],
  (tests) => tests.length,
);

export const selectTestsByStatus = (status: TestSuiteItem["status"]) =>
  createSelector([selectTestSuiteTests], (tests) => tests.filter((test) => test.status === status));

export const selectFirstTest = createSelector([selectTestSuiteTests], (tests) => tests[0] || null);

export const selectCompletedTestIdsByIndex = () =>
  createSelector([testSuiteState], (state) => {
    return state.completedTestIds || [];
  });

export const selectSuiteId = createSelector([testSuiteState], (state) => state.suiteId);

export const selectSuiteName = createSelector([testSuiteState], (state) => state.suiteName);

export const selectTestSuiteOverview = createSelector([testSuiteState], (state) => state.overview);

export const selectTestSuiteOverviewEnabled = createSelector(
  [selectTestSuiteOverview],
  (overview) => isOverviewEnabled(overview),
);

export const selectTestSuiteSessionId = createSelector(
  [testSuiteState],
  (state) => state.sessionId,
);

export const selectSessionResultReplacement = createSelector(
  [testSuiteState],
  (state) => state.sessionResultReplacement,
);

export const selectReplacesTestSuiteTestId = createSelector(
  [selectSessionResultReplacement, (_state: RootState, currentTestId: string) => currentTestId],
  (replacement, currentTestId) => replacement[currentTestId],
);

export const selectCompletedTestIds = createSelector(
  [testSuiteState],
  (state) => state.completedTestIds || [],
);

export const selectAutoPlay = createSelector([testSuiteState], (state) => state.autoPlay);

export const selectAutoPlayCountdown = createSelector(
  [testSuiteState],
  (state) => state.autoPlayCountdown,
);

export const selectSkipNextAutoPlay = createSelector(
  [testSuiteState],
  (state) => state.skipNextAutoPlay,
);

export const selectSuiteRunStartedAt = createSelector(
  [testSuiteState],
  (state) => state.suiteRunStartedAt,
);

export const selectSuiteRunTimestamps = createSelector([testSuiteState], (state) => ({
  startedAt: state.suiteRunStartedAt,
  endedAt: state.suiteRunEndedAt,
}));

export const selectIsSuiteRunning = createSelector(
  [selectSuiteRunTimestamps],
  ({ startedAt, endedAt }) => !!startedAt && !endedAt,
);

export const selectTestSuiteSharedConfiguration = createSelector(
  [testSuiteState],
  (state) => state.sharedTestConfiguration,
);

export const selectPendingPlayAfterSave = createSelector(
  [testSuiteState],
  (state) => state.pendingPlayAfterSave,
);

export const selectFirstTestCompleted = createSelector(
  [testSuiteState],
  (state) => state.firstTestCompleted,
);

export const selectCalibrationEnabledTestId = createSelector(
  [testSuiteState],
  (state) => state.calibrationEnabledTestId,
);

export const selectTestSuiteVirtualAssistantConfig = createSelector(
  [selectTestSuiteSharedConfiguration],
  (sharedConfig) => sharedConfig?.virtualAssistanceConfig || null,
);

export const selectTestSuiteVirtualAssistantOptions = createSelector(
  [selectTestSuiteTests, selectSelectedTestIndex],
  (tests, selectedTestIndex) => {
    if (selectedTestIndex !== null) {
      const opts = tests[selectedTestIndex]?.availableConfigurations?.virtualAssistantOptions;
      if (opts) return opts;
    }
    for (const test of tests) {
      const opts = test.availableConfigurations?.virtualAssistantOptions;
      if (opts) return opts;
    }
    return null;
  },
);
